import { defer } from 'react-router-dom'
import { isAppStateReady } from 'src/state/store'
import { getActiveCompanySettings, logger } from '$utils'
import useWorkforceStore from '../../store'
import { AuthModule, PayrollSystem } from '$constants'

export default async function loader({ params }) {
  logger.debug(
    '[loader] About to await AppState being ready in EditStaffMember loader'
  )
  await isAppStateReady()
  logger.debug(
    '[loader] AppState should now be ready in EditStaffMember loader'
  )

  const { hasManagerPermission, payrollSystemPreference } =
    getActiveCompanySettings()

  //We need to check the connectivity if it has XeroAu or XeroNz because we also need to get
  //the third party employee information if there is a third party connection
  const isThirdPartyPayrollSystemConnected =
    (payrollSystemPreference === PayrollSystem.XeroAu ||
      payrollSystemPreference === PayrollSystem.XeroNz) &&
    (await useWorkforceStore
      .getState()
      .checkThirdPartyConnectionStatus(AuthModule.Time))

  const getStaffMember = params.staffMemberId
    ? useWorkforceStore
        .getState()
        .getStaffMember(params.staffMemberId)
        .then((result) => {
          if (
            isThirdPartyPayrollSystemConnected &&
            result?.thirdPartyEmployeeId
          ) {
            return useWorkforceStore
              .getState()
              .getPayrollEmployee(
                result.thirdPartyEmployeeId,
                payrollSystemPreference as PayrollSystem
              )
          }
        })
    : null

  const getPosStaffMembers = useWorkforceStore.getState().getPosStaff(false)

  const getStaffRoles = useWorkforceStore.getState().getStaffRoles(false)

  const getStaffMembers = useWorkforceStore.getState().getStaffMembers(false)

  const getPayClassifications = useWorkforceStore
    .getState()
    .getPayClassifications()

  //We only want to do this for XeroAu as calendars are not available for XeroNz
  const getCalendars =
    isThirdPartyPayrollSystemConnected &&
    payrollSystemPreference === PayrollSystem.XeroAu
      ? useWorkforceStore
          .getState()
          .getCalendars(payrollSystemPreference as PayrollSystem)
      : []

  //We only want to do this for XeroAu as earnings rates are not available for XeroNz
  const getEarningsRates =
    isThirdPartyPayrollSystemConnected &&
    payrollSystemPreference === PayrollSystem.XeroAu
      ? useWorkforceStore
          .getState()
          .getEarningsRates(payrollSystemPreference as PayrollSystem)
      : []

  //- This manager is checked here and not in the service because it needs to be called from
  //the RequestEmployeeInformation, which is a public page, and it also needs to be called
  //from the PayrollTab in the EditStaffMember page which is only accessible for managers
  //- We only want to do this for XeroAu as super funds are not available for XeroNz
  const getPayrollSuperFunds =
    isThirdPartyPayrollSystemConnected &&
    hasManagerPermission &&
    payrollSystemPreference === PayrollSystem.XeroAu
      ? useWorkforceStore
          .getState()
          .getPayrollSuperFunds(payrollSystemPreference as PayrollSystem)
      : []

  const getSuperFunds = useWorkforceStore.getState().getSuperFunds()

  const getUnavailabilitiesByStaffMemberId = useWorkforceStore
    .getState()
    .getUnavailabilitiesByStaffMemberId(params.staffMemberId)

  const getLeaveTypesByStaffMemberId = useWorkforceStore
    .getState()
    .getLeaveTypesByStaffMemberId(params.staffMemberId)

  const getLeaveRequestsByStaffMemberId = useWorkforceStore
    .getState()
    .getLeaveRequestsByStaffMemberId(params.staffMemberId)

  return defer({
    data: Promise.all([
      getStaffMember,
      getPosStaffMembers,
      getStaffRoles,
      getStaffMembers,
      getPayClassifications,
      getCalendars,
      getEarningsRates,
      getPayrollSuperFunds,
      getSuperFunds,
      getUnavailabilitiesByStaffMemberId,
      getLeaveTypesByStaffMemberId,
      getLeaveRequestsByStaffMemberId,
    ])
      .then(() => true)
      .catch((error) => {
        logger.error('Edit Staff Member Promise.all error', error)
        return false
      }),
  })
}
